import type { LucideIcon } from 'lucide-react'
import {
  Banknote,
  BarChart3,
  BookOpen,
  Bot,
  Building,
  Building2,
  CalendarCheck,
  CalendarClock,
  Calculator,
  Clock,
  Coins,
  CreditCard,
  FileSignature,
  FileText,
  FileUp,
  HeartPulse,
  History,
  IdCard,
  Landmark,
  LayoutGrid,
  Link as LinkIcon,
  ListTree,
  Lock,
  Mail,
  MapPin,
  Palette,
  PieChart,
  Receipt,
  ReceiptText,
  Scale,
  ScrollText,
  Settings,
  Settings2,
  ShieldCheck,
  Sparkles,
  TrendingDown,
  TrendingUp,
  UploadCloud,
  User,
  UserCog,
  Users,
  Wallet,
  Workflow,
} from 'lucide-react'
import type { PermissionCode, Role } from '@/auth/permissions'

/**
 * Sidebar layout model - mirrors the legacy dashboard.html sidebar:
 *
 *   standalone      → flat items rendered at the top of the sidebar
 *   statements…     → collapsible accordion groups (only one open at a time)
 *   platform        → platform-only items (shown at bottom when applicable)
 *
 * Accordion behaviour + sub-group (Allocation Schedules) lives in Sidebar.tsx.
 */
export type MenuGroup =
  | 'standalone'
  | 'statements'
  | 'accounting'
  | 'banking'
  | 'invoicing'
  | 'payroll'
  | 'tax'
  | 'account'
  | 'platform'
  | 'footer'

export type SubGroup =
  | 'allocation-schedules'
  | 'invoicing-ar'
  | 'invoicing-ap'
  | 'payroll-people'
  | 'payroll-time'
  | 'payroll-comp'
  | 'payroll-benefits'
  | 'payroll-payments'
  | 'tax-filings'
  | 'tax-sales'

export type MenuItem = {
  label: string
  /** Path under the React basename. Write '/dashboard', NOT '/app/dashboard'. */
  path: string
  icon: LucideIcon
  permission?: PermissionCode
  /**
   * Subscription / feature-flag gate. When set, the item is hidden
   * unless the tenant's feature map (loaded via useFeatures) returns
   * true for this name. Stacks with `permission` (both must pass).
   */
  feature?: string
  role?: Role
  platformOnly?: boolean
  group: MenuGroup
  /** Nest this item under a sub-accordion inside its parent group. */
  subGroup?: SubGroup
  /** Filled by the page port that ships this entry. */
  ported?: boolean
  /** Hidden when the product is locked to small-business scope (SMB_ONLY). */
  smbHidden?: boolean
}

export type SidebarGroupMeta = {
  key: Exclude<MenuGroup, 'standalone' | 'platform'>
  label: string
  icon: LucideIcon
}

export type SidebarSubGroupMeta = {
  key: SubGroup
  label: string
  icon: LucideIcon
  parent: MenuGroup
  /** Permission gate for the whole sub-group header. */
  permission?: PermissionCode
}

export const sidebarGroups: SidebarGroupMeta[] = [
  { key: 'statements',  label: 'Statements',         icon: FileText },
  { key: 'accounting',  label: 'Accounting',         icon: Calculator },
  { key: 'banking',     label: 'Banking',            icon: Landmark },
  { key: 'invoicing',   label: 'Invoicing (AP-AR)',  icon: Receipt },
  { key: 'payroll',     label: 'Payroll',            icon: Wallet },
  { key: 'tax',         label: 'Tax',                icon: ReceiptText },
  { key: 'account',     label: 'Account',            icon: Settings },
]

export const sidebarSubGroups: SidebarSubGroupMeta[] = [
  {
    key: 'allocation-schedules',
    label: 'Allocation Schedules',
    icon: TrendingDown,
    parent: 'accounting',
    permission: 'accounting.allocations',
  },
  {
    key: 'invoicing-ar',
    label: 'Accounts Receivable',
    icon: TrendingUp,
    parent: 'invoicing',
    permission: 'invoicing.invoices.view',
  },
  {
    key: 'invoicing-ap',
    label: 'Accounts Payable',
    icon: TrendingDown,
    parent: 'invoicing',
    permission: 'invoicing.bills.view',
  },
]

export const menuItems: MenuItem[] = [
  // ── Standalone (top of sidebar, no accordion) ──────────────────────────
  { label: 'Dashboard',         path: '/dashboard',         icon: LayoutGrid,      permission: 'dashboard.view',                 group: 'standalone', ported: true },
  { label: 'Smart Chat',        path: '/chatbot',           icon: Bot,             permission: 'ai.chatbot',                     group: 'standalone', ported: true },
  { label: 'AI Agents',         path: '/agents',            icon: Sparkles,        permission: 'agents.view',                    feature: 'ai_agents',                      group: 'standalone', ported: true },
  { label: 'Workflows',         path: '/workflows',         icon: Workflow,        permission: 'workflows.view',                 feature: 'workflows_engine',               group: 'standalone', ported: true },
  { label: 'Budget Planning',   path: '/budget-planning',   icon: Wallet,          permission: 'accounting.budget',              group: 'standalone', ported: true },
  { label: 'Approvals',         path: '/approvals',         icon: ShieldCheck,     permission: 'approvals.inbox.view',           feature: 'approvals_engine',               group: 'standalone', ported: true },
  { label: 'Inbox',             path: '/inbox',             icon: Mail,            permission: 'inbox.view',                     group: 'standalone', ported: true },

  // ── Statements ─────────────────────────────────────────────────────────
  { label: 'Process Statements',path: '/upload',            icon: UploadCloud,     permission: 'statements.upload',              group: 'statements', ported: true },
  { label: 'View Statements',   path: '/view-statements',   icon: FileText,        permission: 'statements.view',                group: 'statements', ported: true },
  { label: 'Analyze Statements',path: '/analyze-statements',icon: PieChart,        permission: 'statements.analyze',             group: 'statements', ported: true },
  { label: 'Data History',      path: '/data-history',      icon: History,         permission: 'statements.history',             group: 'statements', ported: true },

  // ── Accounting ─────────────────────────────────────────────────────────
  { label: 'Manage Accruals',   path: '/accrual-schedule',  icon: CalendarClock,   permission: 'accounting.accruals',            group: 'accounting', ported: true },
  { label: 'Journal Entries',   path: '/journal-entry',     icon: BookOpen,        permission: 'accounting.journal_entries',     group: 'accounting', ported: true },
  { label: 'Depreciation',      path: '/fixed-assets',      icon: Building,        permission: 'accounting.allocations',         group: 'accounting', subGroup: 'allocation-schedules', ported: true },
  { label: 'Prepaid Amortization', path: '/prepaid',        icon: Clock,           permission: 'accounting.allocations',         group: 'accounting', subGroup: 'allocation-schedules', ported: true },
  { label: 'Debt Amortization', path: '/loan-amortization', icon: Banknote,        permission: 'accounting.allocations',         group: 'accounting', subGroup: 'allocation-schedules', ported: true },
  { label: 'Financial Reports', path: '/reporting',         icon: BarChart3,       permission: 'accounting.reporting',           group: 'accounting', ported: true },
  { label: 'Chart of Accounts', path: '/coa',               icon: ListTree,        permission: 'accounting.coa',                 group: 'accounting', ported: true },
  { label: 'Reconciliation',    path: '/reconcile',         icon: Scale,           permission: 'accounting.reconcile',           group: 'accounting', ported: true },
  { label: 'TB Import',         path: '/tb-import',         icon: FileUp,          permission: 'accounting.tb_import',           group: 'accounting', ported: true },
  { label: 'Year-End Close',    path: '/year-end-close',    icon: Lock,            permission: 'accounting.year_end_close',      group: 'accounting', ported: true },
  { label: 'Reporting Config',  path: '/reporting-config',  icon: Settings2,       permission: 'accounting.reporting_config',    group: 'accounting', ported: true },

  // ── Banking ────────────────────────────────────────────────────────────
  { label: 'Bank Feeds',        path: '/plaid',             icon: LinkIcon,        permission: 'banking.plaid',                  feature: 'bank_feeds',                     group: 'banking', ported: true },

  // ── Invoicing ──────────────────────────────────────────────────────────
  { label: 'Invoices',          path: '/invoicing/invoices',  icon: FileText,      permission: 'invoicing.invoices.view',        feature: 'ar_invoicing',                   group: 'invoicing', subGroup: 'invoicing-ar', ported: true },
  { label: 'Customers',         path: '/invoicing/customers', icon: Users,         permission: 'invoicing.customers.view',       feature: 'ar_invoicing',                   group: 'invoicing', subGroup: 'invoicing-ar', ported: true },
  { label: 'Payments',          path: '/invoicing/payments',  icon: Banknote,      permission: 'invoicing.payouts.view',         feature: 'payments_dashboard',             group: 'invoicing', subGroup: 'invoicing-ar', ported: true },
  { label: 'Recurring',         path: '/invoicing/recurring', icon: History,       permission: 'invoicing.recurring.manage',     feature: 'recurring_invoices',             group: 'invoicing', subGroup: 'invoicing-ar', ported: true },
  { label: 'AR Aging',          path: '/invoicing/reports/ar-aging', icon: BarChart3, permission: 'invoicing.ar.aging.view',     feature: 'ar_invoicing',                   group: 'invoicing', subGroup: 'invoicing-ar', ported: true },
  { label: 'Bills',             path: '/invoicing/bills',   icon: FileText,        permission: 'invoicing.bills.view',           feature: 'ap_bills',                       group: 'invoicing', subGroup: 'invoicing-ap', ported: true },
  { label: 'Vendors',           path: '/invoicing/vendors', icon: Users,           permission: 'invoicing.vendors.view',         feature: 'ap_bills',                       group: 'invoicing', subGroup: 'invoicing-ap', ported: true },
  { label: 'AP Aging',          path: '/invoicing/reports/ap-aging', icon: BarChart3, permission: 'invoicing.ap.aging.view',     feature: 'ap_bills',                       group: 'invoicing', subGroup: 'invoicing-ap', ported: true },
  { label: 'Approval Policies', path: '/approvals/policies', icon: ShieldCheck,   permission: 'approvals.policies.view',        feature: 'approvals_engine',               group: 'invoicing', ported: true },
  { label: 'My Delegations',    path: '/approvals/delegations', icon: UserCog,    permission: 'approvals.delegate',             feature: 'approvals_engine',               group: 'invoicing', ported: true },

  // ── Payroll (Workforce OS) ─────────────────────────────────────────────
  { label: 'Payroll Dashboard', path: '/payroll',              icon: LayoutGrid, permission: 'payroll.dashboard.view', feature: 'workforce_os', group: 'payroll', ported: true },
  { label: 'Run Payroll',       path: '/payroll/runs',         icon: Wallet,     permission: 'payroll.runs.view',      feature: 'workforce_os', group: 'payroll', ported: true },
  { label: 'People',            path: '/payroll/people',       icon: Users,      permission: 'payroll.employees.view', feature: 'workforce_os', group: 'payroll', ported: true },
  { label: 'Time & Leave',      path: '/payroll/time-leave',   icon: Clock,      permission: 'payroll.time.view',      feature: 'workforce_os', group: 'payroll', ported: true },
  { label: 'Compensation',      path: '/payroll/compensation', icon: Coins,      permission: 'payroll.compensation.view', feature: 'workforce_os', group: 'payroll', ported: true },
  { label: 'Benefits',          path: '/payroll/benefits',     icon: HeartPulse, permission: 'payroll.benefits.view',  feature: 'workforce_os', group: 'payroll', ported: true },
  { label: 'Payments',          path: '/payroll/payments',     icon: Banknote,   permission: 'payroll.payments.view',  feature: 'workforce_os', group: 'payroll', ported: true },
  { label: 'Payroll Reports',   path: '/payroll/reports',      icon: BarChart3,  permission: 'payroll.reports.view',   feature: 'workforce_os', group: 'payroll', ported: true },
  { label: 'Payroll Settings',  path: '/payroll/setup',        icon: Settings,   permission: 'payroll.settings.view',  feature: 'workforce_os', group: 'payroll', ported: true },

  // ── Tax (Tax OS) ───────────────────────────────────────────────────────
  { label: 'Tax Dashboard',     path: '/tax',                 icon: LayoutGrid,    permission: 'tax.dashboard.view',     feature: 'tax_os',    group: 'tax', ported: true },
  { label: 'Tax Calendar',      path: '/tax/calendar',        icon: CalendarCheck, permission: 'tax.calendar.view',      feature: 'tax_os',    group: 'tax', ported: true },
  { label: 'Filings',           path: '/tax/filings-hub',     icon: FileSignature, permission: 'tax.filings.view',       feature: 'tax_os',    group: 'tax', ported: true },
  { label: 'Tax Payments',      path: '/tax/payments',        icon: Banknote,      permission: 'tax.payments.view',      feature: 'tax_os',    group: 'tax', ported: true },
  { label: 'Sales Tax',         path: '/tax/sales',           icon: MapPin,        permission: 'tax.sales.view',         feature: 'tax_sales', group: 'tax', ported: true },
  { label: 'Compliance',        path: '/tax/compliance',      icon: Scale,         permission: 'tax.reconciliation.view',feature: 'tax_os',    group: 'tax', ported: true },
  { label: 'Tax Setup',         path: '/tax/setup',           icon: Settings,      permission: 'tax.settings.view',      feature: 'tax_os',    group: 'tax', ported: true },

  // ── Account ────────────────────────────────────────────────────────────
  { label: 'Profile',           path: '/profile',           icon: User,            permission: 'profile.view',                   group: 'account', ported: true },
  { label: 'Team Management',   path: '/team-management',   icon: Users,           permission: 'team.view',                      group: 'account', ported: true },
  { label: 'Company Settings',  path: '/company',           icon: Building2,       permission: 'tenant.settings.view',           group: 'account', ported: true },
  { label: 'Branding',          path: '/branding',          icon: Palette,         permission: 'tenant.settings.view',           group: 'account', ported: true },
  { label: 'Workspaces',        path: '/account/workspaces', icon: Building2,      permission: 'profile.view',                   group: 'account', ported: true },
  { label: 'My Payslips',       path: '/account/my-payslips', icon: ScrollText,    permission: 'me.payslips.view',               group: 'account', ported: true },
  { label: 'My Tax Forms',      path: '/account/my-tax-forms',icon: IdCard,        permission: 'me.tax_forms.view',              group: 'account', ported: true },
  { label: 'My W-4',            path: '/account/my-w4',       icon: FileSignature, permission: 'me.w4.manage',                   group: 'account', ported: true },
  { label: 'My Direct Deposit', path: '/account/my-direct-deposit', icon: Banknote, permission: 'me.direct_deposit.manage',     group: 'account', ported: true },
  { label: 'Webhooks',          path: '/account/webhooks',    icon: LinkIcon,      permission: 'webhooks.view',                  group: 'account', ported: true, smbHidden: true },

  // ── Footer (icon-only row pinned to sidebar bottom) ────────────────────
  { label: 'Subscriptions',     path: '/subscriptions',     icon: CreditCard,      permission: 'billing.view',                   group: 'footer', ported: true },
  { label: 'Contact us',        path: '/contact-sales',     icon: Mail,            permission: 'support.contact_sales',          group: 'footer', ported: true },
  { label: 'User Guide',        path: '/guide',             icon: BookOpen,                                                       group: 'footer', ported: true },
]
